import { Car, Gauge, Calendar, Users, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Vehicle } from "@/hooks/useVehicles";
import { VEHICLE_CATEGORIES, FUEL_TYPES } from "@/hooks/useVehicles";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface VehicleCardProps {
  vehicle: Vehicle;
  onClick: () => void;
  driversCount?: number;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  active: { label: "Actif", variant: "default" },
  maintenance: { label: "En maintenance", variant: "secondary" },
  sold: { label: "Vendu", variant: "outline" },
  scrapped: { label: "Réformé", variant: "destructive" },
};

const categoryColors: Record<string, string> = {
  vl: "bg-blue-500/10 text-blue-600",
  vu: "bg-orange-500/10 text-orange-600",
  pl: "bg-purple-500/10 text-purple-600",
  two_wheels: "bg-green-500/10 text-green-600",
  other: "bg-gray-500/10 text-gray-600",
};

export function VehicleCard({ vehicle, onClick, driversCount = 0 }: VehicleCardProps) {
  const complianceColor = vehicle.compliance_score >= 80 
    ? "text-green-600" 
    : vehicle.compliance_score >= 50 
      ? "text-amber-600" 
      : "text-red-600";

  const complianceProgressColor = vehicle.compliance_score >= 80
    ? "bg-green-500"
    : vehicle.compliance_score >= 50
      ? "bg-amber-500"
      : "bg-red-500";

  const statusInfo = statusConfig[vehicle.status] || statusConfig.active;

  return (
    <Card 
      className="group cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-primary/30"
      onClick={onClick}
    >
      <CardContent className="p-4">
        {/* Header with badges */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className={cn("text-xs", categoryColors[vehicle.category])}>
              {VEHICLE_CATEGORIES[vehicle.category]}
            </Badge>
            <Badge variant={statusInfo.variant} className="text-xs">
              {statusInfo.label}
            </Badge>
          </div>
          <div className={cn("text-sm font-semibold", complianceColor)}>
            {vehicle.compliance_score}%
          </div>
        </div>

        {/* License plate as main identifier */}
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-muted px-3 py-1.5 rounded-md border-2 border-foreground/20 font-mono font-bold text-lg tracking-wider">
            {vehicle.license_plate}
          </div>
        </div>

        {/* Brand + Model */}
        <h3 className="font-semibold text-foreground mb-1 line-clamp-1 group-hover:text-primary transition-colors">
          {vehicle.brand} {vehicle.model}
        </h3>

        {/* Vehicle info */}
        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-3">
          {vehicle.fuel_type && (
            <div className="flex items-center gap-1">
              <Car className="h-3.5 w-3.5" />
              <span>{FUEL_TYPES[vehicle.fuel_type]}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Gauge className="h-3.5 w-3.5" />
            <span>{new Intl.NumberFormat("fr-FR").format(vehicle.current_mileage)} km</span>
          </div>
          {driversCount > 0 && (
            <div className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              <span>{driversCount} conducteur{driversCount > 1 ? "s" : ""}</span>
            </div>
          )}
        </div>

        {/* First registration date */}
        {vehicle.first_registration_date && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
            <Calendar className="h-3.5 w-3.5 shrink-0" />
            <span>
              Mise en circulation : {format(new Date(vehicle.first_registration_date), "MMMM yyyy", { locale: fr })}
            </span>
          </div>
        )}

        {/* Compliance progress */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Conformité documents</span>
            <span className={cn("font-medium", complianceColor)}>
              {vehicle.compliance_score}%
            </span>
          </div>
          <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className={cn("absolute left-0 top-0 h-full rounded-full transition-all", complianceProgressColor)}
              style={{ width: `${vehicle.compliance_score}%` }}
            />
          </div>
        </div>

        {/* Hover action */}
        <div className="flex justify-end mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
            Voir le véhicule
            <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
